import { Dialog } from '@headlessui/react';
import { motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Product {
  id: number;
  name: string;
  image: string;
  description: string;
  category: string;
  price?: number;
  origin?: string;
  usedFor?: string[];
  traditionallyAyurvedicMedicine?: string[];
}

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

export function ProductModal({ isOpen, onClose, product }: ProductModalProps) {
  if (!product) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-4xl"
        >
          <Dialog.Panel className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="relative">
              <button
                onClick={onClose}
                className="absolute right-3 top-3 z-10 p-2 rounded-full bg-white shadow hover:bg-gray-100"
              >
                <XMarkIcon className="h-6 w-6 text-gray-700" />
              </button>

              <div className="flex flex-col lg:flex-row max-h-[90vh] overflow-y-auto">
                {/* Image Section */}
                <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-auto">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content Section */}
                <div className="w-full lg:w-1/2 p-5 sm:p-8 max-h-[80vh] overflow-y-auto">
                  <Dialog.Title className="text-2xl font-bold mb-4">
                    {product.name}
                  </Dialog.Title>

                  <div className="space-y-6 text-sm sm:text-base">
                    {/* Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-orange-700 mb-2">
                        Health Benefits:
                      </h3>
                      <p className="text-gray-600">{product.description}</p>
                    </div>

                    {/* Origin */}
                    {product.origin && (
                      <div>
                        <h3 className="text-lg font-semibold text-orange-700 mb-2">
                          Natural and Traditional:
                        </h3>
                        <p className="text-gray-700">{product.origin}</p>
                      </div>
                    )}

                    {/* Used For */}
                    {product.usedFor && product.usedFor.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-indigo-700 mb-2">
                          Targeted Use:
                        </h3>
                        <ul className="list-disc pl-5 space-y-1">
                          {product.usedFor.map((use, index) => (
                            <li key={index} className="text-gray-700">{use}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Ayurvedic Medicine */}
                    {product.traditionallyAyurvedicMedicine && product.traditionallyAyurvedicMedicine.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-green-700 mb-2">
                          Traditional Ayurvedic Medicine
                        </h3>
                        <ul className="list-disc pl-5 space-y-1">
                          {product.traditionallyAyurvedicMedicine.map((item, index) => (
                            <li key={index} className="text-gray-700">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </motion.div>
      </div>
    </Dialog>
  );
}
